import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import Style from './availableFormPage.module.scss';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import TablePageLayout from 'src/components/tablePageLayout/TablePageLayout';
import { getStudyRolesQueryKey, useGetStudyRolesQuery } from 'src/services/api/api-client/UserRolesQuery';
import { AppTable } from 'uikit/table/AppTable';
import { useCallback, useMemo } from 'react';
import { IFormDto, IStudyRoleDto, StudyRoleClaimValue } from 'src/services/api/api-client';
import { getFormsQueryKey, useGetFormsQuery } from 'src/services/api/api-client/FormsQuery';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { AppCheckboxInput } from 'uikit/inputs';
import _ from 'lodash';
import { makeFormAvailableToRole, makeFormUnavailableToRole } from 'src/services/api/api-client/UserRolesClient';
import { showServerErrorToast } from 'src/components/toast/toast-helper';

export const AvailableFormsPage = () => {
  const { t } = useTranslation('dev');
  const queryClient = useQueryClient();
  const study = useStudy();

  const rolesQuery = useGetStudyRolesQuery({ studyId: study?.id, includeForms: true });
  const studyForms = useGetFormsQuery({ studyId: study!.id }, { enabled: !!study?.id });

  const changeFormAccess = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>, roleId: string, formId: number) => {
      try {
        if (e.target.checked) {
          await makeFormAvailableToRole(roleId, formId);
        } else {
          await makeFormUnavailableToRole(roleId, formId);
        }

        await queryClient.invalidateQueries(getStudyRolesQueryKey());
        await queryClient.invalidateQueries(getFormsQueryKey({ studyId: study!.id }));
      } catch (error) {
        showServerErrorToast(error);
      }
    },
    [queryClient, study],
  );

  const tableData = useMemo(() => _.orderBy(studyForms.data, (x) => x.type) ?? [], [studyForms.data]);

  const rowDataTable = useReactTable<IFormDto>({
    data: tableData,
    manualSorting: false,
    columns: useRolesColumns(rolesQuery.data ?? [], changeFormAccess),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TablePageLayout className={Style.tablePage}>
      <TablePageLayout.Header>
        <TablePageLayout.Header.Row>
          <TablePageLayout.Header.Title text={t('Admin.UserRoles.Caption')} />
        </TablePageLayout.Header.Row>
      </TablePageLayout.Header>
      <TablePageLayout.TableContainer loading={rolesQuery.isLoading || rolesQuery.isPreviousData}>
        <AppTable
          table={rowDataTable}
          placeholder={{
            hide: rolesQuery.isLoading || rolesQuery.isPreviousData,
            text: t('Admin.UserRoles.No_Data'),
          }}
        />
      </TablePageLayout.TableContainer>
    </TablePageLayout>
  );
};

const useRolesColumns = (
  roles: IStudyRoleDto[],
  onChangePermission: (e: React.ChangeEvent<HTMLInputElement>, roleId: string, formId: number) => Promise<void>,
): ColumnDef<IFormDto, any>[] => {
  return useMemo(
    () =>
      [
        {
          accessorKey: 'permission',
          header: '',
          enableSorting: false,
          size: 240,
          meta: {
            cellClassName: Style.permissionCell,
          },
          cell: ({ row }) => {
            return (
              <div className={Style.permissionColumn}>
                <div>{row.original.type}</div>
              </div>
            );
          },
        },
        ...roles?.map((role, i) => ({
          id: role.id,
          accessorKey: role.name,
          header: () => role.name,
          enableSorting: false,
          size: 120,
          cell: ({ row }: any) => {
            const form: IFormDto = row.original;
            return (
              <div className={Style.checkboxWrapper}>
                <AppCheckboxInput
                  disabled={role.claims.includes(StudyRoleClaimValue.StudyConfigure)}
                  checked={role.availableForms.map((x) => x.id).includes(form.id)}
                  onChange={(e) => onChangePermission(e, role.id, form.id)}
                />
              </div>
            );
          },
          meta: {
            isColored: !(i % 2),
            headerClassName: Style.header,
            cellClassName: Style.cellClassName,
          },
        })),
        {
          accessorKey: 'dummy',
          header: '',
          enableSorting: false,
          meta: {
            isColored: !(roles.length % 2),
            dontUseHeaderWidth: true,
            cellClassName: Style.cellClassName,
          },
        },
      ] satisfies ColumnDef<IFormDto, any>[],
    [onChangePermission, roles],
  );
};
